<template lang="pug">
  CommonLoadBox(:loaded="loaded")
    template(slot='skeleton')
      TaskSkeleton(:moduleCount="2")
    template(slot='module')
      .task-module-container(v-if="data && data.length > 0")
        .task-module-item(
          v-for="(items, index) in data"
          :key="index"
          :id="items.key"
          :class="{'new-module': newModule, 'new-style': newStyle }"
        )
          .header
            .title(v-if="newStyle")
              img.title-img(v-if="items.titleIcon" :src="items.titleIcon" alt="")
              span(v-else) {{ items.mainTitle }}
            .title(v-else) {{ moduleTitle }}
          bounty-coupon(
            :double-information="getDoubleInformation(items)"
            :new-module="newModule"
            :data="items.taskCouponList"
            :preparing="preparing"
            :moduleKey="items.key"
            @bounty="$emit('bounty')"
            @refreshTasks="refreshTasks"
          )
</template>

<script>
import { env } from '@hb/NBridge/lib/h5';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { getTokenAndKey, getCityInfo, debounce } from '@/utils/index';
import { mapBusinessInfo } from '@/utils/mappers';
import { TASK_STATUS_V2 } from '@/const/task';
import { getTaskListV2 } from '@/api/bounty';
import { getVersion } from '@/utils/systemCode';
import { ParamsUtil } from '@/utils/params';
import eventBus from '@/const/eventBus.js';
import CommonLoadBox from '@/components/common-load-box';
import { taskGroupListAdapter } from '../common/helper';
import BountyCoupon from './bounty-coupon';
import TaskSkeleton from '../skeleton-cpns/task-skeleton';
import { FULI_MODULE_NAV, WELFARE_VERSION } from '../consts';

export default {
  name: 'BountyTask',
  components: {
    BountyCoupon,
    CommonLoadBox,
    TaskSkeleton,
  },
  props: {
    newModule: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      data: [],
      preparing: true,
      showTask: true,
      // 记录弹窗任务列表第一个任务
      firstTaskCode: '',
      // 是否加载完成
      loaded: false,
    };
  },
  inject: ['newTaskScene', 'taskScene'],
  computed: {
    newStyle() {
      return this.newTaskScene();
    },
    moduleTitle() {
      if (this.newModule) {
        return '赚奖励金';
      }
      return '做任务赚奖励金';
    },
  },
  created() {
    // 初始化获取任务列表
    this.getTasks();
  },
  async mounted() {
    // 监听刷新任务列表事件
    eventBus.$on('refreshTaskList', () => {
      this.refreshTasks();
    });

    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    setTimeout(() => {
      if (this.preparing) {
        this.preparing = false;
      }
    }, 2000);
  },
  destroyed() {
    // 移除页面可见性变化监听
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    refreshTasks() {
      this.getTasks({ isRefresh: true });
    },
    getDoubleInformation(items) {
      return {
        bannerAdvertisementUnitId: items.bannerAdvertisementUnitId || '',
        bannerTaskRelationCouponInfo: items.bannerTaskRelationCouponInfo || {},
      };
    },
    getTasks: debounce(async function foo(options) {
      const { isRefresh } = options || {};
      const { token } = await getTokenAndKey();
      const cityInfo = await getCityInfo();
      const subSceneNameEn = this.newStyle
        ? 'platform_points_order_return'
        : 'platform_points_pointshp';
      return getTaskListV2({
        token,
        channelId: env.platformCode,
        sceneNameEn: 'platform_points',
        subSceneNameEn,
        taskStatusList: [TASK_STATUS_V2.INIT, TASK_STATUS_V2.RUNNING],
        queryUnrewardedTasks: true,
        version: await getVersion(),
        newWelfare: this.taskScene() === WELFARE_VERSION.NEW_TASK_SCENE_V2,
        taskPopupCode: this.firstTaskCode,
        pageAdsource: ParamsUtil.getParam('adsource') || '',
        ...cityInfo,
      })
        .then(async (v) => {
          this.loaded = true;
          // 将任务弹窗数据发送出去
          if (v && v.data && v.data.taskPopupInfoDTOS) {
            const { taskCode } = v.data.taskPopupInfoDTOS[0] || {};
            this.firstTaskCode = taskCode;
            eventBus.$emit('sendTaskPopupData', v.data.taskPopupInfoDTOS || []);
          }

          if (
            v
            && v.data
            && v.data.taskGroupInfoDTOS
            && v.data.taskGroupInfoDTOS.length
          ) {
            // 缓存全量数据到 window（解决bounty组件先渲染场景下，惊喜任务组件晚渲染的问题）
            window.__TASK_DATA_CACHE__ = v.data.taskGroupInfoDTOS;
            eventBus.$emit('taskDataLoaded', v.data.taskGroupInfoDTOS);

            const myTaskData = v.data.taskGroupInfoDTOS.filter(
              (item) => ['welfare_browse_tasks', 'welfare_order_completion_task'].includes(item.moduleKey),
            );

            // 解构成老数据结构
            const oldData = myTaskData.flatMap(
              (item) => item.taskCouponList.map((task) => ({
                mainTitle: item.mainTitle, // 保留外层标题
                ...task, // 展开任务详情
              })),
            );

            // 单单返新版本会返回单单返任务，用户手动做任务时要把单单返类型的任务过滤掉
            const list = this.newTaskScene()
              ? myTaskData.filter(
                (item) => item.taskActivityType !== 10,
              )
              : myTaskData;

            // 任务列表数据处理
            this.data = await taskGroupListAdapter(
              { list, isRefresh },
              () => { this.refreshTasks(); },
            );
            (oldData || []).forEach((task) => {
              setTimeout(() => {
                UBT.moduleExpose({
                  pageId: 'platform_bounty',
                  categoryId: 'platform',
                  moduleId: 'platform_click.task',
                  contentId: 'platform_click.task',
                  exposureTimes: '1',
                  businessInfo: {},
                });
                UBT.moduleExpose({
                  pageId: 'platform_bounty',
                  categoryId: 'market',
                  moduleId: 'market_task',
                  contentId: 'market_task',
                  exposureTimes: '1',
                  businessInfo: mapBusinessInfo(task),
                });
              });
            });
            const version = await getVersion();
            if (version >= '6.63.0') {
              const videoList = oldData.filter((x) => x.taskActivityType === 8);
              if (videoList.length > 0) {
                if (videoList[0].advertCode) {
                  const json = JSON.stringify({
                    classMap: 'hellobike/marketing/rewardVideo',
                    method: 'preloadRewardVideo',
                    params: JSON.stringify({
                      adUnitId: videoList[0].advertCode, // 资源位id
                    }),
                    callbackId: 'preloadRewardVideoCallbackId',
                  });
                  if (env.isAndroid) {
                    window.AppFunctions.callNative(json);
                  } else if (env.isIOS) {
                    window.webkit.messageHandlers.callNative.postMessage(json);
                  }
                }
              }
            }
          } else {
            this.showTask = false;
          }
        })
        .catch((e) => {
          eventBus.$emit('taskDataLoadError', e);
          if (e) {
            this.showTask = false;
          }
        })
        .then(() => {
          this.preparing = false;
          if (!this.showTask) {
            this.$emit('hideTab', FULI_MODULE_NAV.welfare_task_new);
          }
        });
    }, 500, true),
    // 页面可见性变化
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.getTasks();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.task-module-container {
  .page-title {
    height: 0.27rem;
    margin: 0.1rem 0 0.04rem 0.09rem;
  }

  .task-module-item {
    width: 100%;
    padding: 0.12rem 0.16rem 0.12rem;
    background-color: white;
    border-radius: 0.12rem;
    margin-bottom: 0.08rem;

    &:last-child {
      margin-bottom: 0;
    }

    .header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .title {
        margin-bottom: 0.12rem;
        display: flex;
        font-size: 0.16rem;
        font-weight: 500;
        color: #242729;
      }
    }

    &.new-style {
      padding: 0.14rem 0.12rem 0.12rem;

      .title {
        margin-bottom: 0.14rem;
      }

      .title-img {
        width: auto;
        height: 0.22rem;
      }

      .flex {
        margin-bottom: 0.19rem;
        display: flex;

        .title-img {
          width: 1.31rem;
          height: 0.27rem;
        }

        .task-desc {
          margin-left: 0.08rem;
          width: 1.12rem;
          height: 0.27rem;
          line-height: 0.32rem;
          color: #5a6066;
          font-family: 'PingFang SC', sans-serif;
          font-size: 0.12rem;
        }
      }
    }

    &.new-module {
      padding: 0.16rem;

      .header {
        .title {
          margin-bottom: 0.16rem;
          color: #333;
          font-size: 0.16rem;
        }
      }
    }
  }
}
</style>
